/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchBranchesByCompanyId,
  saveBranch,
  updateBranch,
} from "../store/slices/branchSlice";
import { fetchCompanyByType } from "../store/slices/companySlice";
import Layout from "../common/components/Layout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Branch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const { list: branches = [] } = useSelector((state) => state.branch || {});
  const { list: companyList = [] } = useSelector((state) => state.companyTypeList || {});
  const companyId = localStorage.getItem("companyId") || "";
  const role = localStorage.getItem("role") || "";

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingBranch, setPendingBranch] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [filters, setFilters] = useState({ 
    branchName: '', 
    parentCompany: '', 
    parentBranch: '', 
    region: 'All', 
    status: 'All' 
  });

  const [formData, setFormData] = useState({
    id: "", branchName: "", region: "Luzon", parentId: "", companyId: "", isActive: true,
  });

  /* ===================== PAGINATION & SORT STATE ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "branchName", direction: "asc" });

  useEffect(() => {
    if (companyId) {
      dispatch(fetchBranchesByCompanyId({ companyId, role }));
      dispatch(fetchCompanyByType());
    }
  }, [dispatch, companyId, role]);

  useEffect(() => { setCurrentPage(1); }, [filters, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-2 opacity-25" style={{ fontSize: '0.6rem' }}></i>;
    return sortConfig.direction === "asc" 
      ? <i className="bi bi-sort-down ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i> 
      : <i className="bi bi-sort-up ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({ id: "", branchName: "", region: "Luzon", parentId: "", companyId: companyId, isActive: true });
    setShowModal(true);
  };

  const handleOpenEdit = (branch) => {
    setIsEditMode(true);
    setFormData({
      id: branch.id, branchName: branch.branchName || "", region: branch.region || "Luzon",
      parentId: branch.parentId || "", companyId: branch.companyID || "", isActive: branch.isActive ?? true,
    });
    console.log(branch)
    setShowModal(true);
  };

  const handleStatusToggle = (branch) => {
    if (branch.isActive) {
      setPendingBranch(branch);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(branch);
    }
  };

  const executeStatusUpdate = async (branch) => {
    try {
      await dispatch(updateBranch({ 
        id: branch.id, 
        data: { isActive: !branch.isActive }, 
        companyId, 
        role 
      })).unwrap();
      toast.success("Status updated successfully!");
      setShowConfirm(false);
    } catch (err) {
      toast.error("Update failed.");
    }
  };

const handleSave = async (e) => {
    e.preventDefault();
    const data = { ...formData, parentId: formData.parentId || null, companyId: formData.companyId || companyId };
    const action = formData.id ? updateBranch({ id: formData.id, data, companyId, role }) : saveBranch({ data, companyId, role });
    
    try {
      await dispatch(action).unwrap();
      toast.success("Saved successfully!");
      setShowModal(false);
    } catch (err) {
      // 1. Log for debugging
      console.error("Save Error:", err);

      // 2. Extract message from backend. 
      // If using NestJS, it usually comes as 'err.message' or 'err' string
      const errorMessage = err?.message || err || "Operation failed.";
      
      toast.error(errorMessage);
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    const flattenTree = (nodes) => {
      let flat = [];
      nodes.forEach((node) => {
        const { children, ...rest } = node;
        flat.push(rest);
        if (children && children.length > 0) flat = [...flat, ...flattenTree(children)];
      });
      return flat;
    };

    let items = Array.isArray(branches) ? flattenTree(branches) : [];

    items = items.filter((item) => {
      const nameMatch = (item.branchName || "").toLowerCase().includes(filters.branchName.toLowerCase());
      const companyMatch = (item.company?.companyName || "").toLowerCase().includes(filters.parentCompany.toLowerCase());
      const parentMatch = (item.parent?.branchName || "").toLowerCase().includes(filters.parentBranch.toLowerCase());
      const regionMatch = filters.region === "All" || item.region === filters.region;
      const statusMatch = filters.status === "All" || (item.isActive ? "Active" : "Inactive") === filters.status;
      return nameMatch && companyMatch && parentMatch && regionMatch && statusMatch;
    });

    return items.sort((a, b) => {
      let valA, valB;
      const key = sortConfig.key;
      if (key === "company") { valA = (a.company?.companyName || "").toLowerCase(); valB = (b.company?.companyName || "").toLowerCase(); }
      else if (key === "parent") { valA = (a.parent?.branchName || "").toLowerCase(); valB = (b.parent?.branchName || "").toLowerCase(); }
      else { valA = (a[key] || "").toString().toLowerCase(); valB = (b[key] || "").toString().toLowerCase(); }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [branches, filters, sortConfig]);

  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getPaginationGroup = () => {
    const totalDisplayed = 5; 
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const filterStyle = { border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", padding: "10px 14px", fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(5px)", outline: "none" };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; cursor: pointer; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        `}</style>
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>Branches</h1>
            <p className="text-muted small mb-0">Manage organization hierarchy</p>
          </div>
          <button className="btn btn-primary px-4 border-0" style={{ borderRadius: "8px", backgroundColor: "#0f172a", fontWeight: "600", fontSize: '0.85rem' }} onClick={handleOpenAdd}>
            <i className="bi bi-plus-lg me-2"></i>Add New
          </button>
        </div>

        {/* Filter Row */}
        <div className="row g-2 mb-4">
          <div className="col-md-2">
            <input type="text" className="form-control" placeholder="Search Branch..." style={filterStyle} value={filters.branchName} onChange={(e) => setFilters({...filters, branchName: e.target.value})} />
          </div>
          <div className="col-md-3">
            <input type="text" className="form-control" placeholder="Search Parent Company..." style={filterStyle} value={filters.parentCompany} onChange={(e) => setFilters({...filters, parentCompany: e.target.value})} />
          </div>
          <div className="col-md-3">
            <input type="text" className="form-control" placeholder="Search Parent Branch..." style={filterStyle} value={filters.parentBranch} onChange={(e) => setFilters({...filters, parentBranch: e.target.value})} />
          </div>
          <div className="col-md-2">
            <select className="form-select" style={filterStyle} value={filters.region} onChange={(e) => setFilters({...filters, region: e.target.value})}>
              <option value="All">All Regions</option>
              <option>Luzon</option><option>Visayas</option><option>Mindanao</option>
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select" style={filterStyle} value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
              <option value="All">All Status</option>
              <option>Active</option><option>Inactive</option>
            </select>
          </div>
        </div>

        {/* Table Content */}
        
        <div style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  {[
                    { label: "BRANCH NAME", key: "branchName" },
                    { label: "REGION", key: "region" },
                    { label: "PARENT COMPANY", key: "company" },
                    { label: "PARENT BRANCH", key: "parent" },
                    { label: "STATUS", key: "isActive" },
                    { label: "ACTIONS", key: null },
                  ].map((col, idx) => (
                    <th key={idx} className={`py-3 ${idx === 0 ? "ps-4" : ""} border-0 ${col.key === "isActive" ? "text-center" : ""} ${!col.key ? "text-end pe-4" : ""}`}
                      onClick={() => col.key && requestSort(col.key)}>
                      {col.label} {col.key && getSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {currentData.map((item) => (
                  <tr key={item.id} className="align-middle">
                    <td className="ps-4 py-3 fw-bold" style={{ fontSize: "0.85rem", color: '#1e293b' }}>{item.branchName}</td>
                    <td style={{ fontSize: "0.8rem", color: '#334155' }}>{item.region}</td>
                    <td style={{ fontSize: "0.8rem", color: '#334155' }}>{item.company?.companyName || "-"}</td>
                    <td style={{ fontSize: "0.8rem", color: '#334155' }}>{item.parent?.branchName || "-"}</td>
                    <td className="text-center">
                      <span className={`badge rounded-pill ${item.isActive ? "bg-success bg-opacity-10 text-success border border-success" : "bg-danger bg-opacity-10 text-danger border border-danger"} border-opacity-25`} style={{ fontSize: "0.65rem", padding: '6px 12px' }}>
                        {toCamelCase(item.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-primary px-3 fw-bold" style={{ fontSize: "0.75rem", borderRadius: "6px" }} onClick={() => handleOpenEdit(item)}>Edit</button>
                        <button className={`btn btn-sm ${item.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold`} style={{ fontSize: "0.75rem", borderRadius: "6px", minWidth: "95px" }} onClick={() => handleStatusToggle(item)}>
                          {item.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select className="form-select form-select-sm" style={{ width: 'auto', borderRadius: '6px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-muted small">of <strong>{totalItems}</strong> entries</span>
            </div>
            <nav className="d-flex align-items-center">
              <button className={`page-link-custom ${currentPage === 1 ? 'disabled' : ''}`} onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
              {totalPages > 1 && <button className={`page-link-custom ${currentPage === 1 ? 'active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>}
              {currentPage > 3 && totalPages > 5 && <span className="mx-2 text-muted">...</span>}
              {getPaginationGroup().map(p => p !== 1 && p !== totalPages && (
                <button key={p} className={`page-link-custom ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
              ))}
              {currentPage < totalPages - 2 && totalPages > 5 && <span className="mx-2 text-muted">...</span>}
              {totalPages > 0 && <button className={`page-link-custom ${currentPage === totalPages ? 'active' : ''}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>}
              <button className={`page-link-custom ${currentPage === totalPages ? 'disabled' : ''}`} onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg text-center" style={{ width: "400px" }}>
            <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: "3rem" }}></i>
            <h5 className="fw-bold mt-3">Confirm Deactivation</h5>
            <p className="text-muted small">Are you sure you want to deactivate <strong>{pendingBranch?.branchName}</strong>?</p>
            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-danger w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => executeStatusUpdate(pendingBranch)}>Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.4)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg border-0" style={{ width: "550px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold m-0 text-uppercase" style={{ fontSize: '1rem', letterSpacing: '1px' }}>{isEditMode ? "Edit Branch" : "Add New Branch"}</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Branch Name</label>
                  <input type="text" className="form-control" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.branchName} onChange={(e) => setFormData({ ...formData, branchName: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Parent Branch</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.parentId} onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}>
                    <option value="">None (Set as Main)</option>
                    {processedData.filter(b => b.id !== formData.id).map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Region</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.region} onChange={(e) => setFormData({ ...formData, region: e.target.value })}>
                    <option>Luzon</option><option>Visayas</option><option>Mindanao</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Parent Company</label>
                  <select className="form-select" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.companyId} onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}>
                    <option value="">Select Company</option>
                    {companyList.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                  </select>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-light w-100 fw-bold" style={{ borderRadius: "8px" }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary w-100 fw-bold" style={{ backgroundColor: "#0f172a", borderRadius: "8px" }}>{isEditMode ? "Save Changes" : "Create Branch"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Branch;